<template>
  <Modal
    class="tw-translate-y-[-50%] verification-email-modal"
    :max-width="DEFAULT_MAX_WIDTH_MODAL"
    :value="formModel"
    :visible="showRef"
    @close="handleCloseModal"
  >
    <ModalHeader
      class="modal-header"
      :title="`${getUiText('app-settings-domain-verify-email-modal-title-1')} ${
        domain.domain
      }`"
      :hide-close="true"
    />
    <div class="tw-p-10">
      <div class="tw-flex tw-items-center tw-gap-[15px]">
        <span
          class="tw-text-[16px] tw-text-[#211F58] tw-font-[600] tw-leading-[130%]"
          >{{
            getUiText("app-settings-domain-verify-email-modal-sub-title-1")
          }}</span
        >
      </div>
      <div class="tw-mt-[20px]">
        <span
          class="tw-text-[13px] tw-text-[#878787] tw-font-[400] tw-leading-normal"
          >{{
            getComponentUiText(
              "app-settings-domain-verify-email-modal-description-1",
              false,
              { domain: domain.domain },
            )
          }}</span
        >
      </div>
      <BaseForm :enter-event="true" :show="showRef" @on-submit="onSendEmail">
        <!-- Field : Email -->
        <BaseInput
          id="email"
          v-model="formModel"
          :is-required="true"
          :label="getComponentUiText('app-user-profile-form-label-email-1')"
          :placeholder="
            getUiText(
              'app-settings-team-members-organisation-modal-form-placeholder-title',
            )
          "
          :col-input-class="'tw-flex md:tw-w-[80%] lg:tw-w-[520px] !tw-px-0'"
          :label-class="'tw-flex tw-items-center tw-h-[58px] tw-w-[100px] tw-px-0'"
          :class-name="`tw-flex tw-items-center tw-justify-between tw-mx-0 tw-gap-[15px]`"
        >
          <div class="input-container email-container tw-flex tw-w-full">
            <div class="input-username-container tw-flex-1">
              <BFormInput
                v-model="formModel.username"
                type="text"
                :placeholder="
                  getComponentUiText(
                    'app-settings-domain-verify-email-modal-username-placeholder-1',
                  )
                "
                :class="[
                  'form-control input-base-form-input',
                  $v.username.$error ? 'is-invalid' : '',
                ]"
                :state="$v.username.dirty ? $v.username.$error : null"
                @input="handleInputEmail"
              />
              <p
                v-if="
                  $v.username.$errors.length > 0 &&
                  $v.username.$errors.findIndex(
                    (error) => error.$validator !== 'username',
                  ) >= 0
                "
                class="tw-text-[#F7553C] tw-text-[11px] tw-leading-[130%] tw-mt-[10px] tw-mb-0"
              >
                <span>{{ $v.username.$errors?.[0]?.$message }}</span>
              </p>
            </div>
            <span
              class="text-container tw-mx-[16px] tw-text-[13px] tw-leading-[56px]"
              >@</span
            >
            <div class="input-domain-container tw-flex-1">
              <BFormInput
                v-model="formModel.domain"
                type="text"
                :disabled="true"
                class="form-control input-base-form-input"
              />
            </div>
          </div>
        </BaseInput>

        <div
          :class="`button-container tw-flex tw-justify-end ${
            $v.username.$errors.length > 0 ? 'tw-mt-[40px]' : 'tw-mt-[64px]'
          } tw-gap-[14px]`"
        >
          <b-button
            variant="primary"
            class="button-cancel tw-py-[12px] tw-px-[18px] tw-text-[15px] tw-font-medium tw-rounded-[27px]"
            @click="handleCloseModal"
          >
            {{ getUiText("app-settings-domain-delete-modal-action-cancel-1") }}
          </b-button>
          <b-button
            variant="primary"
            class="button-send-email tw-flex tw-items-center tw-h-[43px] tw-bg-[#387DFF] tw-py-[12px] tw-px-[18px] tw-text-[15px] tw-font-medium tw-rounded-[27px]"
            @click="onSendEmail"
          >
            <span
              v-if="!isLoadingAPI"
              ref="innerTextSendMailBtnRef"
              class="tw-text-[13px] tw-font-medium tw-leading-[100%]"
            >
              {{ getUiText("app-settings-send-verification-email-1") }}
            </span>
            <div
              v-else
              :style="{ width: animationWidth + 'px' }"
              class="tw-flex tw-justify-center tw-h-[39px] tw-w-[39px] tw-text-[#FFF]"
            >
              <!-- eslint-disable vue/no-unused-refs -->
              <LottieAnimation
                ref="anim"
                :animation-data="ProgressAnimation"
                :auto-play="true"
                :loop="true"
              />
            </div>
          </b-button>
        </div>
      </BaseForm>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import store from "@/store";
import utilities, {
  IsSpecialCharEmailValid,
  getComponentUiText,
} from "@/utils/utilities";
import { computed, reactive, ref, watch, PropType, Ref } from "vue";

import Modal from "@/components/common/modal.vue";
import ModalHeader from "@/components/common/modalHeader.vue";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { postOrgDomainVerifyById } from "@/services/org";
import BaseInput from "@/components/common/form/baseInput.vue";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import BaseForm from "@/components/common/form/baseForm.vue";
import { showErrorAPI } from "@/services/apiRoutes";
import { UserRoute } from "@/services/apiRoutes/routes";
import { IDomain } from "@/interfaces/org/domain";
import ProgressAnimation from "../../assets/animation/progress_dark.json";
import { LottieAnimation } from "lottie-web-vue";
const DEFAULT_MAX_WIDTH_MODAL = 65;

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
    required: false,
  },
  domain: {
    type: Object as PropType<IDomain>,
    default: () => ({}),
    required: true,
  },
});
const emit = defineEmits(["update:show", "update:send-email"]);

// Custom validation email contain "@".
const validateForEmail = (text: string) => {
  if (text.indexOf("@") === -1) return true;
  else return false;
};

const isLoadingAPI = ref(false);
const innerTextSendMailBtnRef: Ref = ref(null);
const animationWidth = ref(0);
const initialForm = {
  username: "",
  domain: props.domain.domain,
};

// Form modal and validation.
const formModel = reactive({ ...initialForm });

const rules = {
  username: {
    required: helpers.withMessage(
      () => getComponentUiText("app-common-form-value-required-error-1"),
      required,
    ),
    isEmail: helpers.withMessage(
      () =>
        getComponentUiText(
          "app-settings-send-verification-email-invalid-username-error-1",
        ),
      validateForEmail,
    ),
    IsSpecialCharEmailValid: helpers.withMessage(
      () =>
        getComponentUiText(
          "app-settings-organisation-manage-access-modals-email-invalid-error-1",
        ),
      IsSpecialCharEmailValid,
    ),
  },
};

const $v = useVuelidate(rules, formModel);
let showRef = ref(props.show);

watch(
  () => props.show,
  (value) => {
    showRef.value = value;
    if (value) initial();
    else clearWatchAndListener();
  },
);

watch(
  () => props.domain,
  (value) => {
    formModel.domain = value.domain;
    formModel.username = "";
    $v.value.$reset();
  },
  { immediate: true, deep: true },
);

const initial = () => {
  listeningEvent();
};

const listeningEvent = () => {
  EventEmitter.on(EmitterEvents.EVENT_KEYBOARD_ENTER, () => onSendEmail());
};

const clearWatchAndListener = () => {
  EventEmitter.off(EmitterEvents.EVENT_KEYBOARD_ENTER);
};

const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);
const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const successMessage = getUiText(
  "app-settings-domain-send-verification-email-success-1",
);

const onSendEmail = async () => {
  await $v.value.$validate();
  if ($v.value.$invalid) return;
  isLoadingAPI.value = true;
  if (innerTextSendMailBtnRef.value) {
    animationWidth.value =
      innerTextSendMailBtnRef.value.getBoundingClientRect()["width"] as number;
  }
  try {
    const email = `${formModel.username}@${formModel.domain}`;
    const formResponse = await postOrgDomainVerifyById(
      Number(props.domain.domainId),
      email,
    );
    if (formResponse.data?.data) {
      onShowToast(successMessage, ToastVariant.SUCCESS);
      emit("update:send-email");
      isLoadingAPI.value = false;
      handleCloseModal();
    }
  } catch (error: any) {
    isLoadingAPI.value = false;
    showErrorAPI(error, UserRoute.postEmailUserVerify);
  }
};

const onShowToast = (message: string, variant: ToastVariant) => {
  const emitData: IToastMessage = { variant, message };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const clearData = () => {
  for (const key in formModel) {
    if (Array.isArray(formModel[key])) {
      formModel[key].splice(0); // Clear the array content while maintaining reference
    } else {
      formModel[key] = initialForm[key];
    }
  }
  $v.value.$reset();
};

const handleInputEmail = (val: string) => {
  const email = `${formModel.username}@${formModel.domain}`;
  if (email.trim() === "") $v.value.email.$reset();
  else {
    $v.value.username.$touch();
    $v.value.username.$model = val;
  }
};

const handleCloseModal = () => {
  clearData();
  showRef.value = false;
  emit("update:show", showRef.value);
};
</script>

<style lang="scss">
.verification-email-modal {
  max-width: 866px;

  .button-send-email.btn.btn-primary[disabled] {
    background-color: #dbdbdb;
    color: #b7b7b7;
    border-color: #dbdbdb;
  }

  .button-cancel {
    line-height: 100%;
    background-color: #fff;
    color: #f7553c;
    border: 2px solid #f7553c;
  }

  .button-send-email {
    line-height: 100%;
    background-color: #387dff;
    border: 2px solid #387dff;
  }
}

@media (max-width: 420px) {
  .verification-email-modal {
    .input-container {
      width: 100%;
    }
  }
}

@media (max-width: 500px) {
  .verification-email-modal {
    .email-container {
      display: grid;
      grid-template-columns: auto 48px auto;
      grid-template-areas:
        "input-username text"
        "input-domain input-domain";

      .input-username-container {
        grid-area: input-username;
      }

      .input-domain-container {
        margin-top: 10px;
        grid-area: input-domain;
      }

      .text-container {
        grid-area: text;
      }
    }
  }
}
</style>
